import React, { useCallback, useState, useEffect } from 'react';
import { Table, Space, Tooltip, Row, Col, Spin } from 'antd';
import { 
  TableWrapper, 
  B2cEditModal, 
  B2cScheduleEditModal, 
  BillB2CRequestModal, 
  PaymentRequestModal } from 'components/molecules';
import { usePopupManager } from 'react-popup-manager';
import { ColumnsType } from 'antd/lib/table/interface';
import { TotalNumMenu } from 'components/atoms';
import { useLocation, useNavigate } from 'react-router';
import { addNewSearch } from 'utils/utils';
import { CarryOutOutlined } from '@ant-design/icons';
import { useTableInfo } from 'hooks';
import { INIT_PAGESIZE } from 'commons/constants';
import qs from 'query-string';
import * as XLSX from 'xlsx';
import { fetcher } from 'services';

interface FormItemProps {
  title: string | undefined;
  projectList: B2cListDto[] | undefined;  
  searchResult: any;
  hiddenColumns: string[];
  register: boolean;
  useExcelDownload?: boolean;  
  refetch: () => void;
}

const B2CTable = ({title, projectList, searchResult, hiddenColumns, register, useExcelDownload = false, refetch}: FormItemProps): React.ReactElement => {
  /* ======================================================
   * variables
   * ====================================================== */
  const navigate = useNavigate();
  const location = useLocation();
  const { open } = usePopupManager();
  const [rowExpaned, setRowExpaned] = useState<number[]>([]);  
  const searchParams = qs.parse(location.search);
  const [isLoding, setIsLoading] = useState<boolean>(false);   

  const onCellClick = (record) => {
    return {
      onClick: (e) => {
        open(B2cEditModal, {id: Number(record.id), onClose: (isOk)=>{if(isOk) {refetch();}}});            
      },
    };
   };

  const columns: ColumnsType<B2cListDto> = [
    {
      title: '번호',
      dataIndex: 'id',
      key: 'id',
      width: '70px',
      align: 'center',
      onCell: (record) => {
        return {
            onClick: (e) => {
              if (rowExpaned.includes(record.id)) {
                setRowExpaned(rowExpaned.filter(item => item !== record.id) ) 
              } else {
                setRowExpaned(preRowExpaned => [...preRowExpaned, record.id])
              }              
            },
        };
      },
      render: (id, record) => {
        return (
          title == '모집중 과정' && record.student_cnt > 0 && record.min_send_mail == 0 ? (<><span style={{color:"red"}}>◆</span> {id}</>): (<>{id}</>)
          )
      }              
    },  
    {
      title: '구분',
      dataIndex: 'close_yn',
      key: 'close_yn',
      width: '70px',
      align: 'center',
      sorter: (a, b) => a.close_yn.localeCompare(b.close_yn),
      onCell: onCellClick,
      render: (close_yn, record) => {
        return (
          record.status_type==='drop'?'폐강':close_yn==='Y'?'마감':'모집중'
        )
      }       
    },     
    {
      title: '과정명',
      dataIndex: 'lecture_title',
      key: 'lecture_title',
      width: 'auto',
      align: 'left', 
      ellipsis: true,   
      sorter: (a, b) => a.lecture_title.localeCompare(b.lecture_title),
      onCell: onCellClick,
    },  
    {
      title: '메모',
      dataIndex: 'memo',
      key: 'memo',
      width: '50px',
      align: 'center',
      ellipsis: true,
      onCell: onCellClick,
      render: (memo, record) => {
        return (
          (memo)&&(
            <Tooltip placement="topLeft" title={memo}>
              <CarryOutOutlined />
            </Tooltip>
          )
        )
      } 
    },
    {
      title: '회차',
      dataIndex: 'lecture_cha',
      key: 'lecture_cha',
      width: '80px',
      align: 'center',
      ellipsis: true,
      onCell: onCellClick,
      sorter: (a, b) => a.lecture_cha - b.lecture_cha, 
    },
    {
      title: '교육일',
      dataIndex: 'start_date',
      key: 'start_date',
      width: '180px',
      align: 'center',
      ellipsis: true,
      onCell: onCellClick,
      sorter: (a, b) => a.start_date.localeCompare(b.start_date),
      render: (start_date, record) => {
       return (
        <label>{start_date} ~ {record.end_date}</label>
       )
     }   
    },   
    {
     title: '교육단가',
     dataIndex: 'lecture_price',
     key: 'lecture_price',
     width: '80px',
     align: 'right',
     ellipsis: true,
     onCell: onCellClick,
     sorter: (a, b) => a.lecture_price - b.lecture_price, 
     render: (lecture_price, record) => {
      return (
        lecture_price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
      )
    }      
   },   
   {
    title: '미발행매출',
    key: 'not_publish_pirce',
    dataIndex: 'not_publish_pirce',
    width: '100px',
    align: 'right',
    ellipsis: true,
    sorter: (a, b) => a.not_publish_pirce - b.not_publish_pirce, 
    onCell: (record) => {
      return {
          onClick: (e) => {
            if (record.not_publish_pirce > 0)
              onBillRequestPopup(record.id);
          },
      };
    }, 
    render: (not_publish_pirce, record) => {
     return (
      <span>{not_publish_pirce.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</span>
     )
   } 
  },  
   {
    title: '매출금액',
    key: 'max_price',
    dataIndex: 'max_price',
    width: '100px',
    align: 'right',
    onCell: onCellClick,
    sorter: (a, b) => a.max_price - b.max_price, 
    render: (max_price, record) => {
     return (
        max_price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
     )
   }      
  }, 
  /*
  {
    title: '매출이익',
    dataIndex: 'sales_profit',
    key: 'sales_profit',
    width: '120px',
    align: 'right',
    ellipsis: true,
    onCell: onCellClick,
    render: (sales_profit, record) => {
     return (
       sales_profit.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")+` (`+record.profit_margin+`%)`
     )
   }       
  },
  */  
   {
     title: 'A-확정',
     dataIndex: 'bill_cash',
     key: 'bill_cash',
     width: '120px',
     align: 'right',
     ellipsis: true,
     onCell: onCellClick,
     sorter: (a, b) => a.bill_cash - b.bill_cash, 
     render: (bill_cash, record) => {
      return (
        bill_cash.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
      )
    }       
   },  
   {
    title: '시간당단가',
    dataIndex: 'time_price',
    key: 'time_price',
    width: '100px',
    align: 'right',
    ellipsis: true,
    onCell: onCellClick,
    render: (time_price, record) => {
      return (
        time_price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
      )
    }      
  },    
  {
    title: '미지급강사비',
    key: 'calc_out_lecturer_price',
    dataIndex: 'calc_out_lecturer_price',
    width: '90px',
    align: 'right',
    sorter: (a, b) => a.calc_out_lecturer_price - b.calc_out_lecturer_price, 
    onCell: (record) => {
      return {
          onClick: (e) => {
            if (record.calc_out_lecturer_price > 0)
              onPaymentRequestPopup(record.id);
          },
      };
    }, 
    render: (calc_out_lecturer_price, record) => {
     return (
      <span>{calc_out_lecturer_price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</span>
     )
   }   
  },     
  {
   title: '스케줄',
   key: 'schedule',
   width: '80px',
   align: 'center',
   render: (record) => {
    return (
      <>
        <a onClick={(e) => {
          if (rowExpaned.includes(record.id)) {
            setRowExpaned(rowExpaned.filter(item => item !== record.id) ) 
          } else {
            setRowExpaned(preRowExpaned => [...preRowExpaned, record.id])
          }
        }}>{record.schedule.length}건</a>
        {(register) && (
          <>
            &nbsp;|&nbsp;
            <a onClick={(e) => {
              onScheduleEditPopup(record.id, 0, record.lecture_gid, record.lecture_cha);
    
            }}>등록</a>
          </>
        )}
        

      </>
    )
  }
 },  
    {
     title: '신청인원',
     dataIndex: 'student_cnt',
     key: 'student_cnt',
     width: '80px',
     align: 'center',
     ellipsis: true,
     sorter: (a, b) => a.student_cnt - b.student_cnt, 
     onCell: (record) => {
      return {
          onClick: (e) => {
            window.location.href = "/b2c/lecture/student/" + record.id;
          },
      };
    },     
   },
  ];

  /* ======================================================
  * events
  * ====================================================== */ 

 const onClickRegister = () => {
  open(B2cEditModal, {
    id: 0, 
    onClose: (isOk)=>{
      if(isOk) {
        refetch();
      }
    }}); 
 }

 const onChangePageInfo = (pageNo: number, pageSize: number) => {
  navigate({
    ...location,
    search: addNewSearch(location.search, { pageNo, pageSize }),
  });
};  

const onScheduleEditPopup = (group_id: number, id: number, lecture_gid: number, lecture_cha: number) => {
 open(B2cScheduleEditModal, {
   group_id: group_id,
   id: id,
   onClose: (isOk) => {
     if (isOk) {
       refetch();
     }
   },
 }); 
}

const onBillRequestPopup = (id: number) => {
 open(BillB2CRequestModal, {
   id: id,
   student_id: 0,
   onClose: (isOk) => {
     if (isOk) {
       refetch();
     }
   },
 }); 
}

const onExcelDownload = () => {
  setIsLoading(true); 
  fetcher.b2c.postProjectExcelList({
    pageNo: 1,
    pageSize: 0,
    searchValue: searchParams.search ? String(searchParams.search) : '',  
    searchDateStart: searchParams.searchDateStart ? String(searchParams.searchDateStart) : '',   
    searchDateEnd: searchParams.searchDateEnd ? String(searchParams.searchDateEnd) : '',  
    pm: searchParams.pm ? String(searchParams.pm) : '',  
    company: searchParams.company ? String(searchParams.company) : '',   
    crm: searchParams.crm ? String(searchParams.crm) : '',
  })
  .then((res) => {
      switch (res.code) {
          case 200:

          if (res.result) {
            const book = XLSX.utils.book_new();
            const nurses = XLSX.utils.json_to_sheet(
              (res.result.list as LectureExcelListDto[]).map((item, index) => {
                return {
                  '번호' : item.id,
                  '구분' : item.close_yn,
                  '과정명': item.lecture_title,
                  '회차': item.lecture_cha,
                  '교육일': item.start_date?item.start_date+''+'~'+item.end_date:'',
                  '교육단가': item.lecture_price, 
                  '예상매출': item.expect_price,
                  '신청인원': item.student_cnt,
                  '매출금액': item.max_price,
                  'A-확정매출': item.bill_cash,
                  '포인트매출': item.bill_point,
                  '미발행매출': item.not_publish_pirce,
                  '미지급강사비': item.calc_out_lecturer_price,
                  '매출이익': item.sales_profit+' ('+item.profit_margin+'%)',
                  '시간당단가': item.time_price,
                  '스케줄': item.schedule_count,
                  '교육장소': item.location,
                  'PM': item.group_pm,
                  '메모': item.memo,
                }
              }), 
              { 
                header : [
                  '번호',
                  '구분',
                  '과정명',
                  '회차',
                  '교육일',
                  '교육단가', 
                  '예상매출',
                  '신청인원',
                  '매출금액',
                  'A-확정매출',
                  '포인트매출',                  
                  '미발행매출',
                  '미지급강사비',
                  '매출이익',
                  '시간당단가',
                  '스케줄',
                  '교육장소',
                  'PM',
                  '메모',
                ], 
                skipHeader : false 
              } 
            );
        
            nurses["!cols"] = [
              { wpx : 60 } 
            , { wpx : 80 } 
            , { wpx : 240 }  
            , { wpx : 60 }  
            , { wpx : 150 } 
            , { wpx : 100 } 
            , { wpx : 100 } 
            , { wpx : 100 } 
            , { wpx : 100 } 
            , { wpx : 100 } 
            , { wpx : 100 } 
            , { wpx : 100 } 
            , { wpx : 100 } 
            , { wpx : 120 } 
            , { wpx : 100 } 
            , { wpx : 100 } 
            , { wpx : 150 } 
            , { wpx : 80 } 
            , { wpx : 200 } 
            ]
        
            XLSX.utils.book_append_sheet( book, nurses, "NURSES" );
        
            XLSX.writeFile( book, "공개과정.xlsx" ); 
          }

          break;
          default:  
          break;
      };
      setIsLoading(false);         
  });
}  

const onPaymentRequestPopup = (id: number) => {
 open(PaymentRequestModal, {
   id: id,
   group: 'b2c',
   onClose: (isOk) => {
     if (isOk) {
       refetch();
     }
   },
 }); 
}  
  /* ======================================================
  * hooks
  * ====================================================== */
  const { pagination, onTableChange } = useTableInfo({
    pageInfo: {
      pageNo: Number(searchParams.pageNo) || 1,
      pageSize: Number(searchParams.pageSize) || INIT_PAGESIZE,
    },
    datas: projectList,
    totalCount: searchResult.totalCount,
    onChangePageInfo,
  });

  useEffect(() => {
    setRowExpaned([]);
  }, [searchResult.pageNo]); 

  const onExpaned = (val: boolean) => {
    if (val) {
      if (!!projectList) {
        const rows: any[] = [];
        projectList.forEach(record => {
          rows.push(record.id);
        })
  
        setRowExpaned(rows);
      }
    } else {
      setRowExpaned([]);
    }
  };

  /* ======================================================
   * render items
   * ====================================================== */
  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Spin spinning={isLoding} tip="처리중입니다. 잠시만 기다려 주세요." size="large">
        <TotalNumMenu onExpaned={onExpaned} text={title} totalNum={searchResult.totalCount} useRegister={register}  useExcelDownload={useExcelDownload} onUseRegister={onClickRegister} onDownloadExcel={onExcelDownload}/>
        <TableWrapper isSearch={!!searchParams.search || !!searchParams.lecturer_type || !!searchParams.lecturer_category}>
          <Table
            tableLayout="fixed"
            dataSource={projectList}
            columns={columns.filter((item) => hiddenColumns.findIndex((col)=>col === item.key) < 0)}
            pagination={(!!searchResult&&searchResult.pageSize>0)?pagination:false}
            onChange={onTableChange}
            style={{ width: '100%', overflowX: 'auto', minWidth: 600 }}
            className="columnCenterFixTable clickable"
            showSorterTooltip={false}
            rowKey={'id'}
            expandable={{
              expandedRowRender: (record) => (
                <Space direction="vertical" style={{padding:"5px 30px", width:"100%"}} size={3}>
                {record.schedule.map((schedule)=>( 
                  <Row key={schedule.id} style={{padding:"0px 15px", width:"100%"}}>
                    <Col span={3}><a onClick={()=> onScheduleEditPopup(record.id, schedule.id, record.lecture_gid, record.lecture_cha)}>{schedule.time}</a></Col>
                    <Col span={1}>{schedule.lecturer}</Col>
                    <Col span={3}>{schedule.lecture_subject ? schedule.lecture_subject.startsWith('*') ? <span style={{color:'red'}}>{schedule.lecture_subject.slice(1)}</span> : schedule.lecture_subject : ''}</Col>
                    <Col span={2}>{schedule.lecture_place_type}</Col>
                    <Col span={3}>{schedule.lecturer_price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</Col>
                    <Col span={2}>{schedule.pm}</Col>
                    <Col span={2}>{schedule.cm}</Col>
                    <Col span={7} style={{textOverflow:"ellipsis",whiteSpace:"nowrap",overflow:"hidden"}}><Tooltip placement="topLeft" title={schedule.location}>{schedule.location}</Tooltip></Col>
                    <Col span={1} style={{textAlign:"right"}}>{schedule.class_evaluation && schedule.class_evaluation > 0 && schedule.class_evaluation}</Col>
                  </Row>
                ))}
                </Space>
              ),
              columnWidth: 25,
              expandedRowKeys: rowExpaned,
              onExpand: (expaned, record) => { 
                if (expaned) {
                  setRowExpaned(preRowExpaned => [...preRowExpaned, record.id])
                } else {
                  setRowExpaned(rowExpaned.filter(item => item !== record.id) ) 
                }
              },                
            }}
          />
        </TableWrapper> 
      </Spin>
    </Space>
  );
};

export default B2CTable;
